import { z } from 'zod';
import { emailSchema } from './common.js';
// ------------------------------------------------------ integration settings
/**
 * Third-party credentials (Stripe, SMS, email) an admin can set from the back
 * office instead of only through server environment variables.
 *
 * The API never hands a saved secret back out, even to an admin who is
 * allowed to change it — a GET returns whether one is configured and where it
 * came from, never the value. Non-secret fields (a Stripe publishable key, a
 * Twilio "from" number) are not credentials on their own and come back in
 * full, the same way the publishable key already ships to every storefront
 * visitor today.
 */
const settingSource = z.enum(['db', 'env']).nullable();
const secretPreviewSchema = z.object({
    configured: z.boolean(),
    /** Last four characters of the plaintext, for "is this the key I think it is". */
    last4: z.string().nullable(),
    source: settingSource,
});
const stringValueSchema = z.object({ value: z.string().nullable(), source: settingSource });
export const smsProviderSchema = z.enum(['console', 'twilio', 'disabled']);
export const integrationSettingsViewSchema = z.object({
    // Stripe
    stripeSecretKey: secretPreviewSchema,
    stripePublishableKey: stringValueSchema,
    stripeWebhookSecret: secretPreviewSchema,
    /** Whether card checkout is actually available right now, merged config included. */
    cardPaymentEnabled: z.boolean(),
    // SMS
    smsProvider: z.object({ value: smsProviderSchema.nullable(), source: settingSource }),
    twilioAccountSid: stringValueSchema,
    twilioAuthToken: secretPreviewSchema,
    twilioFromNumber: stringValueSchema,
    // Browser calling (t0869) — rides the Twilio account SID/auth token/from
    // number above; these three are what mint a Voice Access Token, so all six
    // together are what "browser calling is configured" means.
    twilioApiKeySid: stringValueSchema,
    twilioApiKeySecret: secretPreviewSchema,
    twilioTwimlAppSid: stringValueSchema,
    voiceCallingEnabled: z.boolean(),
    // Email — SMTP is the outbound fallback; Mailgun (below) is preferred for
    // system email when configured, and is what the Mail module always sends
    // and receives through.
    smtpHost: stringValueSchema,
    smtpPort: z.object({ value: z.number().int().nullable(), source: settingSource }),
    smtpUsername: stringValueSchema,
    smtpPassword: secretPreviewSchema,
    smtpFromAddress: stringValueSchema,
    // Mailgun — powers the in-portal Mail module (send + receive) and, when
    // set, system email in place of SMTP.
    mailgunApiKey: secretPreviewSchema,
    mailgunWebhookSigningKey: secretPreviewSchema,
    mailgunDomain: stringValueSchema,
    mailgunRegion: z.object({ value: z.enum(['us', 'eu']).nullable(), source: settingSource }),
    // AI (t0763) — Anthropic powers text writing help, OpenAI powers image
    // generation. Independent capabilities, each usable without the other.
    anthropicApiKey: secretPreviewSchema,
    openaiApiKey: secretPreviewSchema,
    aiTextEnabled: z.boolean(),
    aiImageEnabled: z.boolean(),
    // Inbound support (t0799) — the URLs an admin pastes into the Twilio
    // console. Read-only: nothing about them is stored, they're computed from
    // PUBLIC_API_ORIGIN, and they change only if that does.
    smsInboundWebhookUrl: z.string(),
    voiceInboundWebhookUrl: z.string(),
    voiceOutboundWebhookUrl: z.string(),
    // Shipping (t0800) — EasyPost. Live carrier rates, label purchase, and
    // tracking updates for shipped orders. No environment fallback; set from
    // the dashboard or the feature is off.
    easypostApiKey: secretPreviewSchema,
    /** The random token embedded in the webhook URL below — EasyPost signs
     *  deliveries to it, the same shape Mailgun's signing key is used for. */
    easypostWebhookSecret: secretPreviewSchema,
    shipFromName: stringValueSchema,
    shipFromStreet1: stringValueSchema,
    shipFromStreet2: stringValueSchema,
    shipFromCity: stringValueSchema,
    shipFromState: stringValueSchema,
    shipFromZip: stringValueSchema,
    shipFromPhone: stringValueSchema,
    /** True only once an API key and a full ship-from address are both set —
     *  enabled-ness is derived, never a flag of its own. */
    shippingLabelsEnabled: z.boolean(),
    /** Read-only, same reasoning as the Twilio webhook URLs above — paste this
     *  into EasyPost's webhook config once an API key is saved. Null until
     *  then, since the path token doesn't exist yet. */
    easypostWebhookUrl: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
/**
 * Tri-state per field: omit it to leave the stored value alone, send a string
 * to replace it, send `null` to clear it back to whatever the environment
 * provides. `.nullish()` is what gives a field that third state — plain
 * `.optional()` could not tell "clear this" from "don't touch this".
 */
export const updateIntegrationSettingsSchema = z.object({
    stripeSecretKey: z.string().min(1).nullish(),
    stripePublishableKey: z.string().startsWith('pk_').nullish(),
    stripeWebhookSecret: z.string().min(1).nullish(),
    smsProvider: smsProviderSchema.nullish(),
    twilioAccountSid: z.string().min(1).nullish(),
    twilioAuthToken: z.string().min(1).nullish(),
    twilioFromNumber: z.string().min(1).nullish(),
    twilioApiKeySid: z.string().startsWith('SK').nullish(),
    twilioApiKeySecret: z.string().min(1).nullish(),
    twilioTwimlAppSid: z.string().startsWith('AP').nullish(),
    smtpHost: z.string().min(1).nullish(),
    smtpPort: z.number().int().min(1).max(65535).nullish(),
    smtpUsername: z.string().min(1).nullish(),
    smtpPassword: z.string().min(1).nullish(),
    smtpFromAddress: emailSchema.nullish(),
    mailgunApiKey: z.string().min(1).nullish(),
    mailgunWebhookSigningKey: z.string().min(1).nullish(),
    mailgunDomain: z.string().min(1).nullish(),
    mailgunRegion: z.enum(['us', 'eu']).nullish(),
    anthropicApiKey: z.string().min(1).nullish(),
    openaiApiKey: z.string().min(1).nullish(),
    easypostApiKey: z.string().min(1).nullish(),
    shipFromName: z.string().trim().min(1).max(150).nullish(),
    shipFromStreet1: z.string().trim().min(1).max(200).nullish(),
    shipFromStreet2: z.string().trim().max(200).nullish(),
    shipFromCity: z.string().trim().min(1).max(100).nullish(),
    shipFromState: z.string().trim().length(2, 'Two-letter state code').toUpperCase().nullish(),
    shipFromZip: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, 'ZIP must be 12345 or 12345-6789')
        .nullish(),
    shipFromPhone: z.string().trim().min(1).max(30).nullish(),
});
//# sourceMappingURL=integrations.js.map